import { uploadFile } from 'src/services/fileUpload';
import http from 'src/services/http';
import { customToast, toastError } from 'src/services/toast';

const ASSISTANT_EMAIL_CONFIG = {
	'distillery-quest':
		'tech@fusionos.ai, natasha@wearedistillery.com, elana@wearedistillery.com',
	'distillery-gcloud-v2':
		'tech@fusionos.ai, natasha@wearedistillery.com, elana@wearedistillery.com, sabrinajohnson@google.com',
	default:
		'tech@fusionos.ai, natasha@wearedistillery.com, elana@wearedistillery.com',
} as const;

interface GenerateReviewCardParams {
	data: any;
	app_url: any;
}

interface GenerateQuoteData {
	name: string;
	company: string;
	position: string;
	product_feedback: string;
	business_enablement: string;
}

interface GenerateQuoteParams {
	data: GenerateQuoteData;
}

interface SendReviewCardEmailParams {
	product_feedback: string;
	business_enablement: string;
	name: string;
	position: string;
	company: string;
	quote: string;
	horizontal_image_url: string;
	square_image_url: string;
	email_to: string;
	user_email: string;
	assistant_alias?: string; // Add assistant_alias parameter
}

interface ProcessLogoParams {
	logo: string;
}

interface ProcessProfilePictureParams {
	profile_picture: string;
}

interface SuccessResponse {
	success: boolean;
	message: string;
}

interface GenerateReviewCardResponse extends SuccessResponse {
	image_url: string;
	data: any;
}

interface GenerateQuoteResponse extends SuccessResponse {
	quote: string | null;
}

interface SendReviewCardEmailResponse extends SuccessResponse {
	details: SendReviewCardEmailParams;
}

interface ProcessLogoResponse extends SuccessResponse {
	logo_processed: string | null;
}

interface ProcessProfilePictureResponse extends SuccessResponse {
	profile_picture_processed: string | null;
}

export type FunctionName = keyof Fns;

type Fns = {
	generate_review_card: (
		params: GenerateReviewCardParams,
	) => Promise<GenerateReviewCardResponse>;
	generate_review_card_without_profile_picture: (
		params: GenerateReviewCardParams,
	) => Promise<GenerateReviewCardResponse>;
	generate_ai_card_with_profile_picture: (
		params: GenerateReviewCardParams,
	) => Promise<GenerateReviewCardResponse>;
	generate_ai_card_without_profile_picture: (
		params: GenerateReviewCardParams,
	) => Promise<GenerateReviewCardResponse>;
	generate_quote: (
		params: GenerateQuoteParams,
	) => Promise<GenerateQuoteResponse>;
	send_review_card_email: (
		params: SendReviewCardEmailParams,
	) => Promise<SendReviewCardEmailResponse>;
	process_logo: (params: ProcessLogoParams) => Promise<ProcessLogoResponse>;
	process_profile_picture: (
		params: ProcessProfilePictureParams,
	) => Promise<ProcessProfilePictureResponse>;
};

export const fns: Fns = {
	generate_review_card: async ({
		data,
		...rest
	}: GenerateReviewCardParams): Promise<GenerateReviewCardResponse> => {
		const reviewCard = await generateReviewCard(data, rest);

		return {
			success: true,
			message: 'Review card generated successfully.',
			data: reviewCard,
		} as GenerateReviewCardResponse;
	},
	generate_review_card_without_profile_picture: async ({
		data,
		...rest
	}: GenerateReviewCardParams): Promise<GenerateReviewCardResponse> => {
		const reviewCard = await generateReviewCard(data, rest);

		return {
			success: true,
			message: 'Review card generated successfully.',
			data: reviewCard,
		} as GenerateReviewCardResponse;
	},
	generate_ai_card_with_profile_picture: async ({
		data,
		...rest
	}: GenerateReviewCardParams): Promise<GenerateReviewCardResponse> => {
		console.log(
			'[generate_ai_card_with_profile_picture] Called with data:',
			data,
		);
		const reviewCard = await generateReviewCard(data, rest);

		return {
			success: true,
			message: 'Review card generated successfully.',
			data: reviewCard,
		} as GenerateReviewCardResponse;
	},
	generate_ai_card_without_profile_picture: async ({
		data,
		...rest
	}: GenerateReviewCardParams): Promise<GenerateReviewCardResponse> => {
		console.log(
			'[generate_ai_card_without_profile_picture] Called with data:',
			data,
		);
		const reviewCard = await generateReviewCard(data, rest);

		return {
			success: true,
			message: 'Review card generated successfully.',
			data: reviewCard,
		} as GenerateReviewCardResponse;
	},

	generate_quote: async ({
		data,
	}: GenerateQuoteParams): Promise<GenerateQuoteResponse> => {
		const quote = await generateQuote(data);

		return {
			success: true,
			message: 'Quote generated successfully.',
			quote: quote,
		};
	},

	send_review_card_email: async ({
		product_feedback,
		business_enablement,
		name,
		position,
		company,
		quote,
		horizontal_image_url,
		square_image_url,
		email_to,
		user_email,
		assistant_alias,
	}: SendReviewCardEmailParams): Promise<SendReviewCardEmailResponse> => {
		// Determine email recipients based on assistant alias
		const emailRecipients =
			assistant_alias && assistant_alias in ASSISTANT_EMAIL_CONFIG
				? ASSISTANT_EMAIL_CONFIG[
						assistant_alias as keyof typeof ASSISTANT_EMAIL_CONFIG
				  ]
				: ASSISTANT_EMAIL_CONFIG.default;

		email_to = emailRecipients;

		console.log('[send_review_card_email] Called with:', {
			product_feedback,
			business_enablement,
			name,
			position,
			company,
			quote,
			horizontal_image_url,
			square_image_url,
			email_to,
			user_email,
			assistant_alias,
		});
		const body = {
			productFeedback: product_feedback,
			businessEnablement: business_enablement,
			userName: name,
			userPosition: position,
			companyName: company,
			quote: quote,
			horizontalImageUrl: horizontal_image_url,
			squareImageUrl: square_image_url,
			emailTo: email_to,
			userEmail: user_email,
			assistantAlias: assistant_alias,
		};

		await http.post('/v1/emails/review-card', body);

		return {
			success: true,
			message: `Email sent to ${name} with the review card attached.`,
			details: {
				product_feedback,
				business_enablement,
				name,
				position,
				company,
				quote,
				horizontal_image_url,
				square_image_url,
				email_to,
				user_email,
			},
		};
	},

	process_logo: async ({
		logo,
	}: ProcessLogoParams): Promise<ProcessLogoResponse> => {
		try {
			const processedLogo = await uploadFile(
				logo,
				true,
				undefined,
				undefined,
				undefined,
				undefined,
				false,
				true,
			);

			return {
				success: true,
				message: 'Logo processed successfully.',
				logo_processed: processedLogo,
			};
		} catch (error: any) {
			if (
				error.response &&
				error.response.data &&
				error.response.data.message
			) {
				console.error('Error processing logo:', error.response.data.message);
				return {
					success: false,
					message: error.response.data.message,
					logo_processed: null,
				};
			} else {
				console.error('Unexpected error processing logo:', error.message);
				return {
					success: false,
					message: 'There was an unexpected error processing logo.',
					logo_processed: null,
				};
			}
		}
	},

	process_profile_picture: async ({
		profile_picture,
	}: ProcessProfilePictureParams): Promise<ProcessProfilePictureResponse> => {
		try {
			const processedProfilePicture = await uploadFile(
				profile_picture,
				true,
				undefined,
				undefined,
				undefined,
				undefined,
				true,
				true,
			);
			return {
				success: true,
				message: 'Profile picture processed successfully.',
				profile_picture_processed: processedProfilePicture,
			};
		} catch (error: any) {
			if (
				error.response &&
				error.response.data &&
				error.response.data.message
			) {
				console.error(
					'Error processing profile picture:',
					error.response.data.message,
				);
				return {
					success: false,
					message: error.response.data.message,
					profile_picture_processed: null,
				};
			} else {
				console.error(
					'Unexpected error processing profile picture:',
					error.message,
				);
				return {
					success: false,
					message:
						'An unexpected error occurred while processing the profile picture.',
					profile_picture_processed: null,
				};
			}
		}
	},
};

export async function generateReviewCard(
	functionArguments: any,
	rest: any,
	// context: any,
): Promise<any> {
	const { app_url, template_id_horizontal, template_id_square } = rest;
	const name = app_url.includes('google_next')
		? `${functionArguments.name}, ${functionArguments.position} at ${functionArguments.company}`
		: functionArguments.name;
	const inputs = {
		product_feedback: functionArguments.product_feedback,
		business_enablement: functionArguments.business_enablement,
		quote: functionArguments.quote,
		name: name,
		position: functionArguments.position,
		company: functionArguments.company,
		logo: functionArguments.logo,
		profile_picture: functionArguments.profile_picture,
		template_id_horizontal,
		template_id_square,
	};

	try {
		const { data: responseData } = await http.post(
			app_url ??
				'/v2/apps/fusion_ai.review_card_generator/execute/generate_review_card_google_next',
			{ inputs },
		);
		const response = await processCallback(responseData.callback);

		return response;
	} catch (error: any) {
		console.error(`[generateReviewCard] exception.`, error);
		throw error;
		// if (error.response?.status === 401) {
		//     throw new HTTPException(error.response.status, error.response.data);
		// }
	}
}

export async function generateQuote(functionArguments: any): Promise<any> {
	const inputs = {
		instructions: 'generate quote',
		variables: {
			product_feedback: functionArguments.product_feedback,
			business_enablement: functionArguments.business_enablement,
			name: functionArguments.name,
			position: functionArguments.position,
			company: functionArguments.company,
		},
	};

	try {
		const { data: responseData } = await http.post(
			'/v2/apps/fusion_ai.review_card_generator/execute/generate_quote',
			{ inputs },
		);

		const response = await processCallback(responseData.callback);

		return response;
	} catch (error: any) {
		console.error(`[generateQuote] exception.`, error);
		throw error;
		// if (error.response?.status === 401) {
		//     throw new HTTPException(error.response.status, error.response.data);
		// }
	}
}

const processCallback = async (
	callbackUrl: string,
	maxRetries = 40,
	retryDelay = 500,
): Promise<any> => {
	let attempt = 0;

	while (attempt < maxRetries) {
		try {
			const response = await http.get(callbackUrl);
			const { status, body } = response.data;

			if (['processing', 'pending', 'partialContent'].includes(status)) {
				attempt++;
				await new Promise((resolve) => setTimeout(resolve, retryDelay));
				continue;
			} else if (['failed'].includes(status)) {
				customToast('Error processing request', 'error');
				throw new Error('Error processing request.');
			} else if (status === 'successful') {
				return body;
			} else {
				console.log(`Unknown status: ${status}`);
				throw new Error(`Unknown status: ${status}`);
			}
		} catch (e) {
			console.error('Exception in processCallback:', e);
			toastError(e);
			throw e;
		}
	}

	throw new Error('Max retries reached without success.');
};
